import { Spinner } from "@/components/reusable";
import type { BatchExtractionStatus } from "@/services/api";
import { PipelineExtractionProgressBanner } from "./PipelineExtractionProgressBanner";

interface PipelineWorkflowProgressBannerProps {
  isUploading: boolean;
  isTriggeringIngestion: boolean;
  isPollingBatch: boolean;
  workflowBatchStatus: BatchExtractionStatus | null;
  workflowFileCount: number;
  uploadedFileCount: number;
  singleFileStatusLabel?: string | null;
  onCancelBatch?: () => void;
}

function fileWord(count: number): string {
  return count === 1 ? "file" : "files";
}

function IndeterminateProgressBar({ label }: { label: string }) {
  return (
    <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-[var(--color-bg-tertiary)]">
      <div
        className="h-full w-1/3 animate-pulse rounded-full bg-[var(--color-brand-primary)]"
        role="progressbar"
        aria-label={label}
        aria-busy="true"
      />
    </div>
  );
}

function DeterminateProgressBar({ percent, label }: { percent: number; label: string }) {
  return (
    <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-[var(--color-bg-tertiary)]">
      <div
        className="h-full rounded-full bg-[var(--color-brand-primary)] transition-all duration-300"
        style={{ width: `${percent}%` }}
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
      />
    </div>
  );
}

function UploadingBanner({
  uploadedFileCount,
  totalFileCount,
  onCancelBatch,
}: {
  uploadedFileCount: number;
  totalFileCount: number;
  onCancelBatch?: () => void;
}) {
  const total = Math.max(totalFileCount, 1);
  const uploaded = Math.min(uploadedFileCount, total);
  const primaryLabel = `Uploading ${uploaded} of ${total} ${fileWord(total)}…`;
  const percent = total > 0 ? Math.min(100, Math.round((uploaded / total) * 100)) : 0;

  return (
    <div className="border-b border-[var(--color-border-light)] bg-[var(--color-bg-primary)] px-3 py-1.5 md:px-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1 space-y-0.5">
          <p
            className="flex items-center gap-1.5 text-xs font-medium text-[var(--color-text-primary)]"
            data-testid="pipeline-workflow-progress"
          >
            <Spinner size="sm" />
            {primaryLabel}
          </p>
          <p className="text-[11px] text-[var(--color-text-secondary)]">
            Files are being uploaded and ingested.
          </p>
        </div>
        {onCancelBatch ? (
          <button
            type="button"
            className="shrink-0 text-[11px] font-semibold text-[var(--color-text-primary)] underline-offset-2 hover:underline"
            onClick={onCancelBatch}
          >
            Cancel batch
          </button>
        ) : null}
      </div>
      <DeterminateProgressBar percent={percent} label={primaryLabel} />
    </div>
  );
}

function TriggeringExtractionBanner({
  workflowFileCount,
  singleFileStatusLabel,
  onCancelBatch,
}: {
  workflowFileCount: number;
  singleFileStatusLabel?: string | null;
  onCancelBatch?: () => void;
}) {
  const total = Math.max(workflowFileCount, 1);
  const primaryLabel = `Ingesting 0 of ${total} ${fileWord(total)}…`;

  return (
    <div className="border-b border-[var(--color-border-light)] bg-[var(--color-bg-primary)] px-3 py-1.5 md:px-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1 space-y-0.5">
          <p
            className="flex items-center gap-1.5 text-xs font-medium text-[var(--color-text-primary)]"
            data-testid="pipeline-workflow-progress"
          >
            <Spinner size="sm" />
            {primaryLabel}
          </p>
          {singleFileStatusLabel ? (
            <p className="text-[11px] text-[var(--color-text-secondary)]">
              {singleFileStatusLabel}
            </p>
          ) : null}
        </div>
        {onCancelBatch ? (
          <button
            type="button"
            className="shrink-0 text-[11px] font-semibold text-[var(--color-text-primary)] underline-offset-2 hover:underline"
            onClick={onCancelBatch}
          >
            Cancel batch
          </button>
        ) : null}
      </div>
      <IndeterminateProgressBar label={primaryLabel} />
    </div>
  );
}

export function PipelineWorkflowProgressBanner({
  isUploading,
  isTriggeringIngestion,
  isPollingBatch,
  workflowBatchStatus,
  workflowFileCount,
  uploadedFileCount,
  singleFileStatusLabel,
  onCancelBatch,
}: PipelineWorkflowProgressBannerProps) {
  if (isUploading) {
    return (
      <UploadingBanner
        uploadedFileCount={uploadedFileCount}
        totalFileCount={workflowFileCount}
        onCancelBatch={onCancelBatch}
      />
    );
  }

  if (isPollingBatch && workflowBatchStatus) {
    return (
      <div data-testid="pipeline-workflow-progress-wrapper">
        <PipelineExtractionProgressBanner
          batchStatus={workflowBatchStatus}
          onCancelBatch={onCancelBatch}
        />
      </div>
    );
  }

  if (isTriggeringIngestion) {
    return (
      <TriggeringExtractionBanner
        workflowFileCount={workflowFileCount}
        singleFileStatusLabel={singleFileStatusLabel}
        onCancelBatch={onCancelBatch}
      />
    );
  }

  return null;
}
